import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { toast } from 'react-toastify';
import { Get, Put } from '../../utils/httpProvider';
import { accountAuthInfoPagingAction } from '../../store/reducers/account';
import { ACCOUNTAUTHINFO_API, WORK_RESULT_API, BaseGatewayImage } from '../../utils/apiUrl';
import { objectShowNumber } from '../../utils/define';

const fmt = value => value === null || value === undefined ? '-' : Number(value).toFixed(2);
const fmtDate = value => value ? new Date(value).toLocaleString('vi-VN') : '-';
const normalize = value => (value || '').toString().trim().toLocaleLowerCase('vi-VN');

const isGuid = str => {
    if (!str || typeof str !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(str.trim());
};

const toInputDate = value => {
    const date = new Date(value);
    const localDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return localDate.toISOString().slice(0, 10);
};

const getDefaultToDate = () => toInputDate(new Date());
const getDefaultFromDate = () => {
    const today = new Date();
    const lastDayOfPreviousMonth = new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    const date = new Date(
        today.getFullYear(),
        today.getMonth() - 1,
        Math.min(today.getDate(), lastDayOfPreviousMonth)
    );
    return toInputDate(date);
};

const getPagedData = response => {
    const data = response?.data ?? response?.Data ?? [];
    return Array.isArray(data) ? data : (data?.$values || []);
};

const getArrayData = response => {
    const data = response?.data ?? response?.Data ?? response ?? [];
    return Array.isArray(data) ? data : (data?.$values || []);
};

const getFullName = accountInfo => {
    if (!accountInfo) return '';
    const auth = accountInfo.accountAuthInfo || accountInfo.AccountAuthInfo || accountInfo;
    const lastName = auth.lastName || auth.LastName || '';
    const middleName = auth.middleName || auth.MiddleName || '';
    const firstName = auth.firstName || auth.FirstName || '';
    const fullName = `${lastName} ${middleName} ${firstName}`.trim().replace(/\s+/g, ' ');
    if (fullName) return fullName;
    const directFullName = accountInfo.fullName || accountInfo.FullName;
    if (directFullName && typeof directFullName === 'string' && !isGuid(directFullName)) {
        return directFullName.trim();
    }
    const uname = accountInfo.account?.username || accountInfo.Account?.username || accountInfo.username;
    if (uname && !isGuid(uname)) return uname;
    return '';
};

const getErrorMessage = error => {
    if (typeof error === 'string') return error;
    if (error?.message) return error.message;
    const validationErrors = error?.errors;
    if (validationErrors) {
        const firstError = Object.values(validationErrors).flat()[0];
        if (firstError) return firstError;
    }
    return 'Không thể lưu đánh giá.';
};

const WorkResultList = () => {
    const [rows, setRows] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [keywordInput, setKeywordInput] = useState('');
    const [keyword, setKeyword] = useState('');
    const [workType, setWorkType] = useState('');
    const [status, setStatus] = useState('');
    const [evaluated, setEvaluated] = useState('false');
    const [fromDateInput, setFromDateInput] = useState(getDefaultFromDate);
    const [toDateInput, setToDateInput] = useState(getDefaultToDate);
    const [fromDate, setFromDate] = useState(getDefaultFromDate);
    const [toDate, setToDate] = useState(getDefaultToDate);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [selected, setSelected] = useState(null);
    const [percent, setPercent] = useState('');
    const [evaluationNote, setEvaluationNote] = useState('');
    const [historyItem, setHistoryItem] = useState(null);
    const [evidenceModalItem, setEvidenceModalItem] = useState(null);
    const [history, setHistory] = useState([]);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);
    const [sortField, setSortField] = useState(null);
    const [sortDirection, setSortDirection] = useState(null);
    const [returnedFilter, setReturnedFilter] = useState('false');
    const [selectedReturnItem, setSelectedReturnItem] = useState(null);
    const [returnComment, setReturnComment] = useState('');
    const [isReturning, setIsReturning] = useState(false);

    const handleSort = (field) => {
        if (sortField === field) {
            if (sortDirection === 'asc') {
                setSortDirection('desc');
            } else if (sortDirection === 'desc') {
                setSortField(null);
                setSortDirection(null);
            } else {
                setSortDirection('asc');
            }
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const renderSortIcon = (field) => {
        if (sortField !== field) {
            return <i className="fas fa-sort text-muted ms-1" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortDirection === 'asc' 
            ? <i className="fas fa-sort-up text-primary ms-1" style={{ fontSize: '0.8rem' }}></i>
            : <i className="fas fa-sort-down text-primary ms-1" style={{ fontSize: '0.8rem' }}></i>;
    };

    const dispatch = useDispatch();
    const { listAccount } = useSelector((state) => state.account);
    const [fetchedAccounts, setFetchedAccounts] = useState([]);

    useEffect(() => {
        dispatch(accountAuthInfoPagingAction({ pageIndex: 1, pageSize: 1000 }));
        const loadAccounts = async () => {
            const response = await Get(ACCOUNTAUTHINFO_API.GET_PAGING, null, {
                pageIndex: 1,
                pageSize: 10000
            });
            setFetchedAccounts(getPagedData(response));
        };
        loadAccounts();
    }, [dispatch]);

    const allAccounts = useMemo(() => {
        const arr = [];
        const seen = new Set();
        [...(listAccount || []), ...(fetchedAccounts || [])].forEach(item => {
            if (!item) return;
            const key = item.id || item.Id || item.accountId || item.AccountId || item.username;
            if (key && !seen.has(String(key).toLowerCase())) {
                seen.add(String(key).toLowerCase());
                arr.push(item);
            } else if (!key) {
                arr.push(item);
            }
        });
        return arr;
    }, [listAccount, fetchedAccounts]);

    useEffect(() => {
        setAccounts(allAccounts);
    }, [allAccounts]);

    const accountMap = useMemo(() => {
        const map = new Map();
        allAccounts.forEach(item => {
            if (!item) return;
            let id = '';
            let username = '';
            let fullName = '';
            let authInfoId = '';
            let positionName = item.positionName || item.PositionName || '';
            let positionId = item.positionId || item.PositionId || '';

            const hasRoles = item.roles || item.Roles;
            if (hasRoles) {
                id = item.id || item.Id;
                username = item.username || item.Username;

                const authInfo = item.accountAuthInfo || item.AccountAuthInfo;
                if (authInfo) {
                    fullName = getFullName(authInfo);
                    authInfoId = authInfo.id || authInfo.Id || '';
                    if (!positionName) positionName = authInfo.positionName || authInfo.PositionName || '';
                    if (!positionId) positionId = authInfo.positionId || authInfo.PositionId || '';
                }
            } else {
                const acc = item.account || item.Account || item.accountResponse || item.AccountResponse;
                id = item.accountId || item.AccountId || acc?.id || acc?.Id;
                username = acc?.username || acc?.Username || item.username || item.Username;
                authInfoId = item.id || item.Id || '';

                fullName = getFullName(item);
                if (acc && !positionName) {
                    positionName = acc.positionName || acc.PositionName || '';
                    positionId = acc.positionId || acc.PositionId || '';
                }
            }

            if (!fullName && username && !isGuid(username)) {
                fullName = username;
            }

            const standardized = {
                id: id ? String(id).trim() : '',
                authInfoId: authInfoId ? String(authInfoId).trim() : '',
                fullName: fullName ? String(fullName).trim() : '',
                username: username ? String(username).trim() : '',
                positionName,
                positionId,
                raw: item
            };

            const keys = [
                standardized.id,
                standardized.authInfoId,
                item.accountId,
                item.AccountId,
                item.id,
                item.Id,
                item.account?.id,
                item.Account?.id,
                item.accountResponse?.id,
                item.AccountResponse?.id,
                standardized.username,
                item.email,
                item.Email
            ];

            keys.forEach(k => {
                if (k) {
                    map.set(String(k).trim().toLowerCase(), standardized);
                }
            });
        });
        return map;
    }, [allAccounts]);

    const matchingUserIds = useMemo(() => {
        const key = normalize(keyword);
        if (!key) return [];
        return accounts
            .filter(account => normalize(`${getFullName(account)} ${account?.account?.username || ''}`).includes(key))
            .map(account => account.accountId)
            .filter(Boolean);
    }, [accounts, keyword]);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const filter = { pageIndex: page, pageSize, fromDate, toDate };
            if (evaluated !== '') filter.isEvaluated = evaluated === 'true';
            if (returnedFilter !== '') filter.isReturned = returnedFilter === 'true';
            if (keyword) {
                filter.keyword = keyword;
                filter.userIds = matchingUserIds;
            }
            if (workType !== '') filter.workType = Number(workType);
            if (status !== '') filter.status = Number(status);

            const response = await Get(WORK_RESULT_API.GET_ALL_PAGING, null, filter);
            if (!response) {
                setRows([]);
                setTotal(0);
                toast.error('Không thể tải danh sách kết quả công việc.');
                return;
            }
            setRows(getPagedData(response));
            setTotal(response.totalRecords ?? response.TotalRecords ?? 0);
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsLoading(false);
        }
    }, [page, pageSize, keyword, matchingUserIds, workType, status, evaluated, returnedFilter, fromDate, toDate]);

    useEffect(() => { load(); }, [load]);

    const displayEmployeeName = row => {
        const uid = (row.userId || '').toString().trim().toLowerCase();
        const empName = (row.employeeName || '').toString().trim().toLowerCase();
        const account = accountMap.get(uid) || (empName ? accountMap.get(empName) : null);
        return account?.fullName || getFullName(account) || (!isGuid(row.employeeName) ? row.employeeName : '') || '-';
    };

    const sortedRows = useMemo(() => {
        if (!sortField || !sortDirection) return rows;
        return [...rows].sort((a, b) => {
            let valA = a[sortField];
            let valB = b[sortField];

            if (sortField === 'userId') {
                valA = displayEmployeeName(a);
                valB = displayEmployeeName(b);
            }

            if (typeof valA === 'string' && typeof valB === 'string') {
                return sortDirection === 'asc'
                    ? valA.localeCompare(valB, 'vi', { sensitivity: 'base' })
                    : valB.localeCompare(valA, 'vi', { sensitivity: 'base' });
            }

            if (valA === null || valA === undefined) valA = 0;
            if (valB === null || valB === undefined) valB = 0;

            const numA = Number(valA);
            const numB = Number(valB);
            if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
                return sortDirection === 'asc' ? numA - numB : numB - numA;
            }

            const strA = (valA ?? '').toString();
            const strB = (valB ?? '').toString();
            return sortDirection === 'asc'
                ? strA.localeCompare(strB, 'vi', { sensitivity: 'base' })
                : strB.localeCompare(strA, 'vi', { sensitivity: 'base' });
        });
    }, [rows, sortField, sortDirection, accountMap]);

    const submitSearch = event => {
        event.preventDefault();
        if (fromDateInput && toDateInput && fromDateInput > toDateInput) {
            toast.warning('Từ ngày không được lớn hơn đến ngày.');
            return;
        }
        setPage(1);
        setKeyword(keywordInput.trim());
        setFromDate(fromDateInput);
        setToDate(toDateInput);
    };

    const resetFilters = () => {
        setKeywordInput('');
        setKeyword('');
        setWorkType('');
        setStatus('');
        setEvaluated('false');
        setReturnedFilter('false');
        const defaultFromDate = getDefaultFromDate();
        const defaultToDate = getDefaultToDate();
        setFromDateInput(defaultFromDate);
        setToDateInput(defaultToDate);
        setFromDate(defaultFromDate);
        setToDate(defaultToDate);
        setPage(1);
    };

    const openEvaluation = row => {
        setSelected(row);
        setPercent(row.evaluationPercent ?? '');
        setEvaluationNote(row.evaluationNote ?? '');
    };

    const closeEvaluation = () => {
        setSelected(null);
        setPercent('');
        setEvaluationNote('');
    };

    const saveEvaluation = async () => {
        if (!selected) return;
        const value = Number(percent);
        if (percent === '' || percent === null || percent === undefined || Number.isNaN(value) || value < 0 || !Number.isInteger(value)) {
            toast.warning('Tỷ lệ đánh giá phải là số nguyên từ 0 trở lên.');
            return;
        }

        setIsSaving(true);
        try {
            const uid = (selected.userId || '').toString().trim().toLowerCase();
            const empName = (selected.employeeName || '').toString().trim().toLowerCase();
            const acc = accountMap.get(uid) || (empName ? accountMap.get(empName) : null);
            const email = acc?.email || acc?.Email || acc?.raw?.email || acc?.raw?.Email || acc?.raw?.accountAuthInfo?.email || '';
            const fullName = acc?.fullName || getFullName(acc) || (!isGuid(selected.employeeName) ? selected.employeeName : '') || '';

            await Put(WORK_RESULT_API.SAVE_EVALUATION, null, {
                workType: selected.workType,
                workId: selected.workId,
                userId: selected.userId,
                evaluationPercent: value,
                evaluationNote: evaluationNote.trim() || null,
                email,
                fullName
            });
            closeEvaluation();
            toast.success('Đã lưu đánh giá thành công.');
            await load();
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsSaving(false);
        }
    };

    const openHistory = async row => {
        setHistoryItem(row);
        setHistory([]);
        setIsHistoryLoading(true);
        try {
            const response = await Get(WORK_RESULT_API.GET_HISTORY, null, {
                workType: row.workType,
                workId: row.workId,
                userId: row.userId
            });
            setHistory(getArrayData(response));
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const closeHistory = () => {
        setHistoryItem(null);
        setHistory([]);
    };

    const openReturnModal = row => {
        setSelectedReturnItem(row);
        setReturnComment(row.returnComment || '');
    };

    const closeReturnModal = () => {
        setSelectedReturnItem(null);
        setReturnComment('');
    };

    const saveReturn = async () => {
        if (!selectedReturnItem) return;
        if (!returnComment.trim()) {
            toast.warning('Vui lòng nhập lý do hoàn trả công việc.');
            return;
        }

        if (returnComment.trim().length > 500) {
            toast.warning('Lý do hoàn trả không được vượt quá 500 ký tự.');
            return;
        }

        setIsReturning(true);
        try {
            const uid = (selectedReturnItem.userId || '').toString().trim().toLowerCase();
            const empName = (selectedReturnItem.employeeName || '').toString().trim().toLowerCase();
            const acc = accountMap.get(uid) || (empName ? accountMap.get(empName) : null);
            const email = acc?.email || acc?.Email || acc?.raw?.email || acc?.raw?.Email || acc?.raw?.accountAuthInfo?.email || '';
            const fullName = acc?.fullName || getFullName(acc) || (!isGuid(selectedReturnItem.employeeName) ? selectedReturnItem.employeeName : '') || '';

            await Put(WORK_RESULT_API.RETURN_TASK, null, {
                workType: selectedReturnItem.workType,
                workId: selectedReturnItem.workId,
                userId: selectedReturnItem.userId,
                returnComment: returnComment.trim(),
                email,
                fullName
            });
            closeReturnModal();
            toast.success('Đã hoàn trả công việc thành công.');
            await load();
        } catch (error) {
            toast.error(getErrorMessage(error));
        } finally {
            setIsReturning(false);
        }
    };

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-lg-12">
                    <div className="card">
                        <div className="card-header">
                            <h3 className="card-title">Kết quả công việc</h3>
                        </div>
                        <div className="card-body">
                            <Form onSubmit={submitSearch} className="mb-3">
                                <div className="d-flex flex-wrap align-items-end">
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 165px' }}>
                                        <Form.Label>Từ ngày</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={fromDateInput}
                                            onChange={event => setFromDateInput(event.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 165px' }}>
                                        <Form.Label>Đến ngày</Form.Label>
                                        <Form.Control
                                            type="date"
                                            value={toDateInput}
                                            onChange={event => setToDateInput(event.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 210px' }}>
                                        <Form.Label>Tìm kiếm</Form.Label>
                                        <Form.Control
                                            value={keywordInput}
                                            placeholder="Tên công việc hoặc thành viên"
                                            onChange={event => setKeywordInput(event.target.value)}
                                        />
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 150px' }}>
                                        <Form.Label>Loại công việc</Form.Label>
                                        <Form.Control as="select" value={workType} onChange={event => { setWorkType(event.target.value); setPage(1); }}>
                                            <option value="">Tất cả</option>
                                            <option value="1">Thường niên</option>
                                            <option value="2">Dự án / đột xuất</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 125px' }}>
                                        <Form.Label>Trạng thái</Form.Label>
                                        <Form.Control as="select" value={status} onChange={event => { setStatus(event.target.value); setPage(1); }}>
                                            <option value="">Tất cả</option>
                                            <option value="3">Hoàn thành</option>
                                            <option value="4">Quá hạn</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 140px' }}>
                                        <Form.Label>Đánh giá</Form.Label>
                                        <Form.Control as="select" value={evaluated} onChange={event => { setEvaluated(event.target.value); setPage(1); }}>
                                            <option value="false">Chưa đánh giá</option>
                                            <option value="true">Đã đánh giá</option>
                                            <option value="">Tất cả</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <Form.Group className="mr-2 mb-2" style={{ flex: '0 0 140px' }}>
                                        <Form.Label>Hoàn trả</Form.Label>
                                        <Form.Control as="select" value={returnedFilter} onChange={event => { setReturnedFilter(event.target.value); setPage(1); }}>
                                            <option value="">Tất cả</option>
                                            <option value="false">Chưa hoàn trả</option>
                                            <option value="true">Đã hoàn trả</option>
                                        </Form.Control>
                                    </Form.Group>
                                    <div className="mb-2 text-nowrap" style={{ flex: '0 0 205px' }}>
                                        <Button type="submit" variant="info" className="mr-2">
                                            <i className="fas fa-search mr-1" /> Tìm kiếm
                                        </Button>
                                        <Button type="button" variant="secondary" onClick={resetFilters}>
                                            <i className="fas fa-undo mr-1" /> Đặt lại
                                        </Button>
                                    </div>
                                </div>
                            </Form>

                            <div className="d-flex justify-content-between align-items-center mb-3">
                                <div className="form-inline">
                                    <label className="mr-2">Hiển thị:</label>
                                    <Form.Control
                                        as="select"
                                        value={pageSize}
                                        onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}
                                    >
                                        {objectShowNumber.map(item => <option key={item.value} value={item.value}>{item.text}</option>)}
                                    </Form.Control>
                                </div>
                                <span className="text-muted">Tổng số: <strong>{total}</strong> công việc</span>
                            </div>

                            <div className="dataTables_wrapper dt-bootstrap4">
                                <Table striped bordered hover responsive style={{ fontSize: '14px' }}>
                                    <thead className="bg-light">
                                        <tr className="text-center">
                                            <th style={{ width: 55 }}>STT</th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('workType')}>
                                                Loại {renderSortIcon('workType')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('workName')}>
                                                Công việc {renderSortIcon('workName')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('userId')}>
                                                Thành viên {renderSortIcon('userId')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('status')}>
                                                Trạng thái {renderSortIcon('status')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('endDate')}>
                                                Hạn hoàn thành {renderSortIcon('endDate')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('baseScore')}>
                                                Điểm trước đánh giá {renderSortIcon('baseScore')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('evaluationPercent')}>
                                                Tỷ lệ {renderSortIcon('evaluationPercent')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('actualScore')}>
                                                Điểm thực nhận {renderSortIcon('actualScore')}
                                            </th>
                                            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort('evaluatedAt')}>
                                                Đánh giá gần nhất {renderSortIcon('evaluatedAt')}
                                            </th>
                                            <th style={{ width: 120 }}>Minh chứng</th>
                                            <th>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!isLoading && sortedRows.map((row, index) => (
                                            <tr key={`${row.workType}-${row.workId}-${row.userId}`}>
                                                <td className="text-center">{(page - 1) * pageSize + index + 1}</td>
                                                <td className="text-center text-nowrap">
                                                    <Badge variant={row.workType === 1 ? 'primary' : 'info'}>
                                                        {row.workType === 1 ? 'Thường niên' : 'Dự án'}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <strong>{row.workName}</strong>
                                                    {row.isReturned && (
                                                        <span className="badge badge-warning ml-2" title={`Lý do hoàn trả: ${row.returnComment || ''}`}>
                                                            <i className="fas fa-undo mr-1" /> Bị hoàn trả
                                                        </span>
                                                    )}
                                                </td>
                                                <td>{displayEmployeeName(row)}</td>
                                                <td className="text-center">
                                                    <Badge variant={row.status === 3 ? 'success' : 'danger'}>
                                                        {row.status === 3 ? 'Hoàn thành' : 'Quá hạn'}
                                                    </Badge>
                                                </td>
                                                <td className="text-center text-nowrap">{fmtDate(row.endDate)}</td>
                                                <td className="text-right">{fmt(row.baseScore)}</td>
                                                <td className="text-center">
                                                    {row.evaluationPercent == null
                                                         ? <Badge variant="secondary">Chưa đánh giá</Badge>
                                                         : <strong className="text-primary">{row.evaluationPercent}%</strong>}
                                                </td>
                                                <td className="text-right font-weight-bold text-success">{fmt(row.actualScore)}</td>
                                                <td className="text-nowrap">
                                                    {row.evaluatedBy || '-'}
                                                    {row.evaluatedAt && <><br /><small className="text-muted">{fmtDate(row.evaluatedAt)}</small></>}
                                                </td>
                                                <td className="text-center text-nowrap">
                                                    {row.evidenceFiles && row.evidenceFiles.length > 0 ? (
                                                        <Button
                                                            size="sm"
                                                            variant="outline-primary"
                                                            onClick={() => setEvidenceModalItem(row)}
                                                            title="Xem danh sách minh chứng"
                                                        >
                                                            <i className="fas fa-paperclip mr-1" />
                                                            Tệp ({row.evidenceFiles.length})
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted" style={{ fontSize: '13px' }}>Không có</span>
                                                    )}
                                                </td>
                                                <td className="text-center text-nowrap">
                                                    <Button size="sm" variant="info" className="mr-1" onClick={() => openEvaluation(row)}>
                                                        <i className="fas fa-star mr-1" /> Đánh giá
                                                    </Button>
                                                    <Button size="sm" variant="warning" className="mr-1" onClick={() => openReturnModal(row)} title="Hoàn trả công việc">
                                                        <i className="fas fa-undo mr-1" /> Hoàn trả
                                                    </Button>
                                                    <Button size="sm" variant="outline-secondary" onClick={() => openHistory(row)}>
                                                        <i className="fas fa-history mr-1" /> Lịch sử
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}
                                        {!isLoading && rows.length === 0 && (
                                            <tr><td colSpan="12" className="text-center text-muted py-4">Không có công việc phù hợp.</td></tr>
                                        )}
                                        {isLoading && (
                                            <tr><td colSpan="12" className="text-center py-5">
                                                <span className="spinner-border text-info" role="status" aria-hidden="true" />
                                                <div className="text-muted mt-2">Đang tải dữ liệu...</div>
                                            </td></tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {total > pageSize && (
                                <div className="d-flex justify-content-center">
                                    <Pagination
                                        activePage={page}
                                        itemsCountPerPage={pageSize}
                                        totalItemsCount={total}
                                        pageRangeDisplayed={5}
                                        onChange={setPage}
                                        itemClass="page-item"
                                        linkClass="page-link"
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={!!selected} onHide={closeEvaluation} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Đánh giá công việc</Modal.Title>
                </Modal.Header>
                {selected && (
                    <Modal.Body>
                        <div className="p-3 mb-3 bg-light rounded">
                            <div className="font-weight-bold text-primary mb-1">{selected.workName}</div>
                            <div>Thành viên: <strong>{displayEmployeeName(selected)}</strong></div>
                            <div>Điểm trước đánh giá: <strong>{fmt(selected.baseScore)}</strong></div>
                        </div>
                        <Form.Group>
                            <Form.Label>Tỷ lệ đánh giá (%) <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="1"
                                value={percent}
                                placeholder="Nhập số nguyên từ 0 trở lên"
                                onChange={event => setPercent(event.target.value)}
                            />
                            <Form.Text className="text-muted">Có thể nhập từ 0% hoặc lớn hơn 100%.</Form.Text>
                        </Form.Group>
                        <Form.Group className="mt-3">
                            <Form.Label>Ghi chú đánh giá</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={evaluationNote}
                                placeholder="Nhập nhận xét / ghi chú cho nhân sự (không bắt buộc)..."
                                onChange={event => setEvaluationNote(event.target.value)}
                            />
                        </Form.Group>
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeEvaluation} disabled={isSaving}>Đóng</Button>
                    <Button variant="primary" onClick={saveEvaluation} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : 'Lưu đánh giá'}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!historyItem} onHide={closeHistory} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Lịch sử đánh giá</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {historyItem && (
                        <div className="mb-3">
                            <div className="font-weight-bold">{historyItem.workName}</div>
                            <div className="text-muted">Thành viên: {displayEmployeeName(historyItem)}</div>
                        </div>
                    )}
                    {isHistoryLoading && (
                        <div className="text-center py-4">
                            <span className="spinner-border text-info" role="status" aria-hidden="true" />
                        </div>
                    )}
                    {!isHistoryLoading && history.length === 0 && (
                        <div className="text-center text-muted py-4">Chưa có lịch sử đánh giá nào.</div>
                    )}
                    {!isHistoryLoading && history.length > 0 && (
                        <Table bordered hover size="sm" style={{ fontSize: '13px' }}>
                            <thead className="bg-light">
                                <tr className="text-center">
                                    <th>STT</th>
                                    <th>Tỷ lệ cũ</th>
                                    <th>Tỷ lệ mới</th>
                                    <th>Điểm cũ</th>
                                    <th>Điểm mới</th>
                                    <th>Người sửa</th>
                                    <th>Thời gian</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((item, index) => (
                                    <tr key={item.id ?? index} className="text-center">
                                        <td>{index + 1}</td>
                                        <td>{item.oldEvaluationPercent == null ? '-' : `${item.oldEvaluationPercent}%`}</td>
                                        <td><strong>{item.newEvaluationPercent == null ? '-' : `${item.newEvaluationPercent}%`}</strong></td>
                                        <td>{fmt(item.oldScore)}</td>
                                        <td><strong>{fmt(item.newScore)}</strong></td>
                                        <td>{item.evaluatedBy || '-'}</td>
                                        <td>{fmtDate(item.evaluatedAt)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeHistory}>Đóng</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!evidenceModalItem} onHide={() => setEvidenceModalItem(null)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="fas fa-paperclip mr-2 text-primary" />
                        Danh sách file minh chứng ({evidenceModalItem?.evidenceFiles?.length || 0} tệp)
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {evidenceModalItem && (
                        <div>
                            <div className="alert alert-info py-2 mb-3">
                                <strong>{evidenceModalItem.workName}</strong> ({evidenceModalItem.workType === 1 ? 'Thường niên' : 'Dự án'})<br />
                                <span style={{ fontSize: '13px' }}>Thành viên thực hiện: <strong>{displayEmployeeName(evidenceModalItem)}</strong></span>
                            </div>
                            {(!evidenceModalItem.evidenceFiles || evidenceModalItem.evidenceFiles.length === 0) ? (
                                <div className="text-center text-muted py-4">Không có tệp minh chứng nào được đính kèm.</div>
                            ) : (
                                <Table bordered hover size="sm" style={{ fontSize: '13px' }}>
                                    <thead className="bg-light">
                                        <tr className="text-center">
                                            <th style={{ width: 50 }}>STT</th>
                                            <th>Tên tệp</th>
                                            <th style={{ width: 120 }}>Dung lượng</th>
                                            <th style={{ width: 120 }}>Thao tác</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {evidenceModalItem.evidenceFiles.map((file, idx) => {
                                            const fileUrl = file.filePath?.startsWith('http') ? file.filePath : `${BaseGatewayImage}${file.filePath?.startsWith('/') ? '' : '/'}${file.filePath}`;
                                            return (
                                                <tr key={file.id || idx}>
                                                    <td className="text-center">{idx + 1}</td>
                                                    <td>
                                                        <i className="fas fa-file mr-2 text-secondary" />
                                                        <strong>{file.fileName}</strong>
                                                    </td>
                                                    <td className="text-center text-muted">
                                                        {file.fileSize ? `${(file.fileSize / 1024).toFixed(1)} KB` : '-'}
                                                    </td>
                                                    <td className="text-center">
                                                        <a
                                                            href={fileUrl}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="btn btn-xs btn-outline-primary"
                                                            download
                                                        >
                                                            <i className="fas fa-download mr-1" /> Tải về
                                                        </a>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setEvidenceModalItem(null)}>Đóng</Button>
                </Modal.Footer>
            </Modal>

            <Modal show={!!selectedReturnItem} onHide={closeReturnModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title className="text-warning">
                        <i className="fas fa-undo mr-2" />
                        Hoàn trả công việc
                    </Modal.Title>
                </Modal.Header>
                {selectedReturnItem && (
                    <Modal.Body>
                        <div className="p-3 mb-3 bg-light rounded border">
                            <div className="font-weight-bold text-primary mb-1">{selectedReturnItem.workName}</div>
                            <div>Loại: <strong>{selectedReturnItem.workType === 1 ? 'Thường niên' : 'Dự án'}</strong></div>
                            <div>Thành viên: <strong>{displayEmployeeName(selectedReturnItem)}</strong></div>
                            <div>Hạn hoàn thành: <strong>{fmtDate(selectedReturnItem.endDate)}</strong></div>
                        </div>
                        <Form.Group>
                            <Form.Label>
                                Lý do hoàn trả <span className="text-danger">*</span>
                                <small className="text-muted ml-2">(Tối đa 500 ký tự)</small>
                            </Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                maxLength={500}
                                value={returnComment}
                                placeholder="Nhập lý do chi tiết yêu cầu nhân sự bổ sung minh chứng hoặc làm lại..."
                                onChange={event => setReturnComment(event.target.value)}
                            />
                            <div className="d-flex justify-content-between mt-1">
                                <Form.Text className="text-muted">
                                    Nhân sự sẽ nhìn thấy lý do này trên bảng Kanban cá nhân.
                                </Form.Text>
                                <small className={returnComment.length > 450 ? 'text-danger font-weight-bold' : 'text-muted'}>
                                    {returnComment.length}/500
                                </small>
                            </div>
                        </Form.Group>
                    </Modal.Body>
                )}
                <Modal.Footer>
                    <Button variant="secondary" onClick={closeReturnModal} disabled={isReturning}>Hủy bỏ</Button>
                    <Button variant="warning" onClick={saveReturn} disabled={isReturning}>
                        {isReturning ? 'Đang xử lý...' : 'Xác nhận hoàn trả'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default WorkResultList;
